import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ActiviteService } from 'src/app/activites/services/activite.service';
import { IEntiteEditComponent } from 'src/app/core/interface/ientite-edit-component';
import { Dico } from 'src/app/core/models/dico';
import { EntiteEditor } from 'src/app/core/models/entite-editor';
import { EntiteSearchor } from 'src/app/core/models/entite-searchor';
import { IdCodeLabel } from 'src/app/core/models/IdCodeLabel';
import { SearchParam } from 'src/app/core/models/search-param';
import { ILoutyEntiteService } from 'src/app/core/services/ilouty-entite-service';
import { ToastMessagesService } from 'src/app/core/services/toast-messages.service';
import { MenuContexteService } from 'src/app/layout/services/menu-contexte.service';
import { ActiviteMembresDto, AssociationMembreActiviteDto } from '../../models/association-membre-activite-dto';
import { AssociationActiviteMembresService } from '../../services/association-activite-membres.service';
import { AssociationActiviteMembresPopupComponent } from '../association-activite-membres-popup/association-activite-membres-popup.component';
import { ACTIVITE_MEMBRES_EDITOR } from 'src/app/core/const/injection-tokens';

@Component({
  selector: 'app-association-activite-membres',
  templateUrl: './association-activite-membres.component.html',
  styleUrls: ['./association-activite-membres.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssociationActiviteMembresComponent implements OnInit, OnChanges, IEntiteEditComponent<ActiviteMembresDto> {
  constructor(
    private associationActiviteMembresService: AssociationActiviteMembresService,
    private toastMessagesService: ToastMessagesService,
    private fb: FormBuilder,
    public route: ActivatedRoute,
    public router: Router,
    // public : le gabarit lit editor.erreursFiche (bandeau, #1083).
    @Inject(ACTIVITE_MEMBRES_EDITOR) public editor: EntiteEditor<ActiviteMembresDto>,
    private activiteService: ActiviteService,
    private menuContexteService: MenuContexteService,
  ) {}
  getSaveConfirmMsg?: (dto: ActiviteMembresDto) => string;
  resetAvantPatchValue?: (dto: ActiviteMembresDto) => void;
  resetApresPatchValue?: (dto: ActiviteMembresDto) => void;
  doAfterSave(_id: number): Promise<any> {
    return new Promise<void>((resolve, _reject) => {
      resolve();
    });
  }
  ignoreCreation?: boolean;
  warnOnReset?: (dto: ActiviteMembresDto) => string;
  downloading: boolean;
  exporting: boolean;
  pdfDispo: boolean;
  label: string;
  forcingKeys: string[];
  reloadAfterSave = true;
  entiteService: ILoutyEntiteService<ActiviteMembresDto, any> = this.associationActiviteMembresService;
  form: FormGroup;
  readonly: boolean;
  saving: boolean;
  reload: () => void;
  consigne?: (control: FormControl) => void;
  @Input() activiteId;
  activiteSearchor: EntiteSearchor<IdCodeLabel>;
  // static : l'en-tête lit le menu du popup dès son premier rendu (#1083).
  @ViewChild(AssociationActiviteMembresPopupComponent, { static: true })
  popupBarAssociationActiviteMembres: AssociationActiviteMembresPopupComponent;

  getBreadcrumbLabel(): string {
    return '';
  }

  buildEditForm(): void {
    this.form = this.fb.group({
      id: [this.activiteId, [Validators.required]],
      debutAct: [{ value: false, disabled: true }],
      finAct: [{ value: false, disabled: true }],
      miseEnSommeilSage: [{ value: false, disabled: true }],
      activiteMembreList: [],
    });
  }

  listenChanges(): void {}

  onActiviteChange() {
    this.activiteId = this.form?.get('id').value;
    this.afterActiviteChange();
  }

  afterActiviteChange() {
    if (this.activiteId) {
      this.associationActiviteMembresService.get(this.activiteId).subscribe((dto) => {
        this.form.patchValue(dto);
        this.form.markAsPristine();
      });
      this.menuContexteService.setContexte([
        {
          codeInterface: 'GA_ACT_MB',
          codeContexte: 'ACTIVITE',
          valeur: this.activiteId,
        },
      ]);
    } else {
      this.form?.reset();
      this.menuContexteService.clearContexte();
    }
  }

  ngOnInit(): void {
    this.editor.init(this);
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (!this.activiteSearchor) {
      this.activiteSearchor = new EntiteSearchor((searchParam: SearchParam<Dico<any>>) => this.activiteService.quickSearch(searchParam), {
        masque: false,
      });
    }

    if (simpleChanges['activiteId']) {
      if (this.activiteId) {
        this.form?.get('id').setValue(this.activiteId);
        this.activiteSearchor.searchById(this.activiteId).then(() => this.activiteSearchor.initSearchParam());
      }
      // Partie commune au changement depuis l'input ou depuis l'interface (form)
      this.afterActiviteChange();
    }
  }

  alertAssoNonSupprimable() {
    this.toastMessagesService.showWarning(
      "Cette association n'est pas supprimable, car des notes de frais concernent ce membre et cette activité. Vous pouvez cependant lui affecter une date de fin.",
    );
  }

  // assoNonSupprimable = (ctrl: FormGroup): boolean => !!ctrl.value['nfAssociees']
  assoNonSupprimable = (_ctrl: FormGroup): boolean => false;

  resumeAsso = (a: AssociationMembreActiviteDto) => a.membre?.label;

  get newAssoDto() {
    return new AssociationMembreActiviteDto({ membre: new IdCodeLabel({ id: null }) });
  }

  get membreAssocieList() {
    return this.form
      .get('activiteMembreList')
      .value?.filter((v) => v.membre?.id)
      .map((v) => v.membre.id);
  }

  save() {
    this.editor.save(this.form.getRawValue());
  }

  get activiteMembres() {
    return this.form.get('activiteMembreList').value ?? [];
  }

  alertModifParFicheActivite() {
    return "Non modifiable depuis cette interface. Passer par la fiche de l'activité.";
  }
}
